// @flow

// Types
import type { OrdersOperation } from '../../types'

// Libs
import _every from 'lodash/every'
import _map from 'lodash/map'
import { push } from 'react-router-redux'

// Constants
import { PROP_CLIENT_SECRET } from 'hfty-model-orders'

// Operator
import operator from '../operator'

// Controller
import controller from 'hfty-ctrl-orders'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

type OperationsArgs = {
  operations$: (state: Object) => Array<OrdersOperation>,
}

export default function* watchOperations({ operations$ }: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, { operations$ })
  }
}

export function* handleOperations({ operations$ }: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      yield call(handleSubmitOperations, { operations$ })
    } else {
      done = yield call(handleCancelOperations, { operations$ })
    }
  }
}

export function* handleCancelOperations({ operations$ }: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)
  const action = yield operator.actionCreators.cancelOperations(operations)
  yield put(action)
  return true
}

export function* handleSubmitOperations({ operations$ }: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations = yield _map(operations, function (operation: OrdersOperation): Object {
    return call(handleSubmitOperation, operation)
  })

  return _every(nextOperations, function (operation: OrdersOperation): boolean {
    return operation.success
  })
}

export function* handleSubmitOperation(operation: OrdersOperation): Generator {
  const { order } = operation.model

  const withLoader = {
    ...operation,
    model: {
      ...operation.model,
      isLoading: true,
    },
    submitting: false,
    success: true,
  }

  const action = operator.actionCreators.updateOperation(withLoader)
  yield put(action)

  const { error, response } = yield call([controller, controller.create], {
    model: order,
  })

  if (error) {
    const nextOperation = {
      ...operation,
      model: {
        ...operation.model,
        isLoading: false,
      },
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      model: {
        ...operation.model,
        [PROP_CLIENT_SECRET]: response[PROP_CLIENT_SECRET],
        isLoading: false,
      },
      submitting: false,
      success: true,
      errors: [],
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)
    yield put(push(`/order-supplies/checkout/${response.id}`))
    return nextOperation
  }
}
